import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import UserStore from 'src/LocalForage/UserStore';
import { useDispatch } from 'react-redux';
import { useFormState, FormState, FormErrors } from '../../../Hooks/Auth/useFormState';
import { useBrandAuthConfig } from '../../../Hooks/Auth/useBrandAuthConfig';
import { ENV_VERIFIED_TRAINER, isBrandActive } from '../../../config/environment.config';
import LayoutSteps from '../LayoutSteps';
import FormInput from '../../../Components/Ui/FormInput';
import FormCheckbox from '../../../Components/FormFields/FormCheckbox';
import useFormValidation from '../../../Hooks/Auth/useFormValidation';
import { useAuthFn } from '../../../Hooks/Auth/useAuthFn';
import useTailwindBrand from '../../../Theme/useTailwindBrand';

interface RegisterProps {
  nextStep: () => void;
  /** Espace client : même étape, mais le compte créé est un compte client. */
  isClient?: boolean;
}

function RegisterFirst({ nextStep, isClient = false }: RegisterProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { handleGoogleSignUp, handleMailSignUp } = useAuthFn({ setLoading })
  const location = useLocation();
  const { form, errors, setField, setErrors, clearError } = useFormState();
  const { validateRegisterForm, validateField } = useFormValidation();
  const { brandConfig, designConfig, getUserObject } = useBrandAuthConfig();
  const { classes } = useTailwindBrand()
  

  useEffect(() => {
    UserStore.getItem('authorization').then((authorization) => {
      if (authorization) {
        UserStore.removeItem('authorization');
      }
    });
  },[]);   

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const updatedForm = { ...form, [name]: value };
      setField(name as keyof FormState, value);
      if (name === 'password' || name === 'cpassword') {
        const passwordValue = name === 'password' ? value : updatedForm.password || '';
        const cpasswordValue = name === 'cpassword' ? value : updatedForm.cpassword || '';

        if (passwordValue && cpasswordValue) {
          const passwordError = validateField('password', passwordValue, updatedForm);
          if (passwordError) {
            setErrors({ ...errors, password: passwordError });
          } else {
            clearError('password');
          }
        } else if (name === 'password' && !passwordValue) {
          setErrors({ ...errors, password: 'Veuillez remplir ce champ' });
        } else if (name === 'cpassword' && !cpasswordValue) {
          setErrors({ ...errors, password: 'Veuillez confirmer votre mot de passe' });
        } else {
          clearError('password');
        }
      } else {
        const error = validateField(name as keyof FormState, value, updatedForm);
        if (error) {
          setErrors({ ...errors, [name]: error });
        } else {
          clearError(name as keyof FormErrors);
        }
      }
    },
    [form, setField, validateField, setErrors, errors, clearError]
  );
  const validate = useCallback((): boolean => {
    const validationErrors = validateRegisterForm(form);
    setErrors(validationErrors);
    return !Object.values(validationErrors).some((error) => error !== '');
  }, [form, validateRegisterForm, setErrors]);
  const handleSubmitMail = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;
      // const state = location.state as { from: string } | undefined;
      
      const actionType = isClient ? undefined : 'SET_TEACHR_INFO';
      const userType = isClient ? 'client' : brandConfig.name;

      const userObject: any = isClient ? { mail: form.email, phone: form.phone, sponsorship: '' } : getUserObject;
      // ?=client / ?=prestataire portent le rôle, pas un code de parrainage
      const searchValue = location.search ? location.search.slice(2) : '';
      userObject.sponsorship = searchValue === 'client' || searchValue === 'prestataire' ? '' : searchValue;

      try {
        await handleMailSignUp(
          e,
          validate,
          setLoading,
          form.email.toLowerCase(),
          form.password,
          !isClient,
          dispatch,
          form.phone,
          userObject,
          actionType,
          userType,
          navigate,
          form.remember
        );

        nextStep();
      } catch (error) {
        console.error('Erreur lors de l\'inscription:', error);
      }
    },
    [validate, isClient, brandConfig.name, form.email, form.phone, form.password, form.remember, getUserObject, location.search, handleMailSignUp, dispatch, navigate, nextStep]
  );

  // Soumission avec Google
  const handleSubmitGoogle = useCallback(async () => {
    // const route = `/${getActiveBrandConfig()}/inscription/next`;
    const actionType = isClient ? undefined : 'SET_TEACHR_INFO';
    const userType = isClient ? 'client' : brandConfig.name;

    const userObject: any = isClient ? { mail: '', phone: '', sponsorship: '' } : getUserObject;
    const searchValue = location.search ? location.search.slice(2) : '';
    userObject.sponsorship = searchValue === 'client' || searchValue === 'prestataire' ? '' : searchValue;

    try {
      await handleGoogleSignUp(
        setLoading,
        !isClient,
        dispatch,
        navigate,
        userObject,
        form.remember,
        actionType,
        userType,
        // route
      );
      // Si succès, passer à l'étape suivante
      nextStep();
    } catch (error) {
      console.error('Erreur lors de l\'inscription Google:', error);
    }
  }, [isClient, brandConfig.name, getUserObject, location.search, handleGoogleSignUp, dispatch, navigate, form.remember, nextStep]);

  return (
    <LayoutSteps
      designConfig={designConfig}
      loading={loading}
      handleSubmit={handleSubmitMail}
      handleGoogleSignUp={handleSubmitGoogle}
      btnText="Suivant"
      stepNumber={1}
      isClient={isClient}
      totalSteps={isClient ? 2 : 3}
      children={
        <div className=" w-full grid lg:grid-cols-2 grid-cols-1 gap-4 relative2" key={1}>
          <FormInput
            label="Adresse e-mail"
            name="email"
            type="text"
            placeholder="john.dupont@email.com"
            value={form.email}
            error={errors.email}
            onChange={handleChange}
            required
            autoComplete="email"
          />
          <FormInput
            label="Numéro de téléphone"
            name="phone"
            type="tel"
            placeholder="06 12 34 56 78"
            value={form.phone}
            error={errors.phone}
            onChange={handleChange}
            required
            autoComplete="tel"
          />
          <FormInput
            label="Mot de passe"
            name="password"
            type={form.showPass ? 'text' : 'password'}
            placeholder="••••••••"
            value={form.password}
            error={errors.password}
            onChange={handleChange}
            showPasswordToggle
            showPassword={form.showPass}
            onTogglePassword={() => setField('showPass', !form.showPass)}
            required
            autoComplete="new-password"
          />
          <FormInput
            label="Confirmer le mot de passe"
            name="cpassword"
            type={form.showCPass ? 'text' : 'password'}
            placeholder="••••••••"
            value={form.cpassword}
            error={errors.password}
            onChange={handleChange}
            showPasswordToggle
            showPassword={form.showCPass}
            onTogglePassword={() => setField('showCPass', !form.showCPass)}
            required
            autoComplete="new-password"
          />
          <FormCheckbox
            label="J'accepte la politique des données."
            classLabel={`${classes.text} font-bold`}
            checked={form.terms}
            error={errors.terms}
            onChange={() => setField('terms', !form.terms)}
            href={`https://${brandConfig.domain}/${isBrandActive(ENV_VERIFIED_TRAINER) ? 'politique-de-confidentialite' : 'policy'}`}
          />
          <FormCheckbox
            label="J'accepte les CGUs et CGVs."
            classLabel={`${classes.text} font-bold`}
            checked={form.ccg}
            error={errors.ccg}
            onChange={() => setField('ccg', !form.ccg)}
            href={`https://${brandConfig.domain}/${isBrandActive(ENV_VERIFIED_TRAINER) ? 'cgu-cgv' : 'cgu_cgv'}`}
          />
          <FormCheckbox
            label="Se souvenir de moi"
            classLabel={`${classes.text} font-bold`}
            checked={form.remember ?? false}
            required={false}
            onChange={() => setField('remember', !form.remember)}
          />
        </div>
      }
    //   childrenRight={designConfig.rightContent}
    />
  );
}

export default RegisterFirst;

